import { useMemo, useState } from 'react';
import { ErrorBanner, Loading } from '../components/ui';
import { num } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface QueueTicket {
  id: number;
  ticketNumber: string;
  title: string;
  queue: string;
  status: string;
  priority: string;
  company: string | null;
  assignedTo: string | null;
  created: string | null;
  lastActivity: string | null;
  due: string | null;
  ageDays: number | null;
}
interface QueuesData {
  queues: string[];
  tickets: QueueTicket[];
  webUrl: string | null;
  fetchedUtc: string;
}

const SERIES = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--series-5)', 'var(--series-6)'];
const AGED = 3;
const isUrgent = (p: string) => /critical|high/i.test(p);
const ageLabel = (d: number | null) => (d === null ? '—' : d < 1 ? `${Math.max(1, Math.round(d * 24))}h` : `${Math.round(d)}d`);

/**
 * Service Delivery › Queues: every Autotask queue and its open tickets on one
 * page, without scrolling sideways. One card per queue — how many are open,
 * split by status, and what needs looking at (unassigned, critical or high,
 * older than three days, the oldest) — in a grid that wraps to the width of
 * the page. Picking a queue lists its tickets in a table whose columns share
 * the width, long text cut short with the full text on hover.
 *
 * Read once and kept until Refresh, like the rest of the page.
 */
export function ServiceQueues() {
  const live = useHeldResource<QueuesData>('/service/queues');
  const [picked, setPicked] = useState<string | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const [sort, setSort] = useState<'age' | 'priority' | 'status'>('age');
  const d = live.data;

  /* One colour per status, the same in every card and the legend. */
  const statuses = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of d?.tickets ?? []) m.set(t.status, (m.get(t.status) ?? 0) + 1);
    return [...m].sort((a, b) => b[1] - a[1]).map(([name]) => name);
  }, [d]);
  const colour = (s: string) => SERIES[statuses.indexOf(s) % SERIES.length]!;

  const queues = useMemo(() => {
    const by = new Map<string, QueueTicket[]>();
    for (const q of d?.queues ?? []) by.set(q, []);
    for (const t of d?.tickets ?? []) (by.get(t.queue) ?? by.set(t.queue, []).get(t.queue)!).push(t);
    return [...by]
      .map(([name, list]) => {
        const status = new Map<string, number>();
        for (const t of list) status.set(t.status, (status.get(t.status) ?? 0) + 1);
        return {
          name,
          list,
          status: [...status].sort((a, b) => statuses.indexOf(a[0]) - statuses.indexOf(b[0])),
          unassigned: list.filter((t) => !t.assignedTo).length,
          urgent: list.filter((t) => isUrgent(t.priority)).length,
          aged: list.filter((t) => (t.ageDays ?? 0) > AGED).length,
          oldest: list.reduce((m, t) => Math.max(m, t.ageDays ?? 0), 0),
        };
      })
      .sort((a, b) => b.list.length - a.list.length || a.name.localeCompare(b.name));
  }, [d, statuses]);

  if (live.loading && !d) return <Loading label="Reading the queues from Autotask" />;
  if (live.error) return <ErrorBanner message={live.error} />;
  if (!d) return null;

  const shown = queues.filter((q) => showEmpty || q.list.length > 0);
  const empty = queues.length - queues.filter((q) => q.list.length > 0).length;
  const open = picked ? queues.find((q) => q.name === picked) : null;
  const rows = open
    ? [...open.list].sort((a, b) =>
        sort === 'age'
          ? (b.ageDays ?? 0) - (a.ageDays ?? 0)
          : sort === 'priority'
            ? Number(isUrgent(b.priority)) - Number(isUrgent(a.priority)) || (b.ageDays ?? 0) - (a.ageDays ?? 0)
            : a.status.localeCompare(b.status) || (b.ageDays ?? 0) - (a.ageDays ?? 0),
      )
    : [];
  const href = (t: QueueTicket) =>
    d.webUrl
      ? `${d.webUrl}Autotask/AutotaskExtend/ExecuteCommand.aspx?${new URLSearchParams({ Code: 'OpenTicketDetail', TicketNumber: t.ticketNumber })}`
      : null;

  return (
    <div className="sq">
      <div className="row-between wrap mb-2">
        <p className="xs muted">
          {num(d.tickets.length)} open tickets across {num(queues.length - empty)} queues
          {empty > 0 && (
            <>
              {' · '}
              <button type="button" className="link-button" onClick={() => setShowEmpty(!showEmpty)}>
                {showEmpty ? 'hide' : 'show'} {empty} empty
              </button>
            </>
          )}
          {' · as of '}
          {new Date(live.fetchedAt ?? d.fetchedUtc).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </p>
        <button type="button" className="btn btn-ghost btn-sm" disabled={live.refreshing} onClick={live.refresh}>
          {live.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
        </button>
      </div>

      <div className="sq-legend mb-2">
        {statuses.map((s) => (
          <span key={s}>
            <i style={{ background: colour(s) }} />
            {s}
          </span>
        ))}
      </div>

      <div className="sq-grid mb-4">
        {shown.map((q) => (
          <button
            key={q.name}
            type="button"
            className={`sq-card${picked === q.name ? ' is-on' : ''}${q.list.length === 0 ? ' is-empty' : ''}`}
            onClick={() => setPicked(picked === q.name ? null : q.name)}
            disabled={q.list.length === 0}
          >
            <span className="sq-head">
              <span className="sq-name" title={q.name}>
                {q.name}
              </span>
              <span className="sq-count">{num(q.list.length)}</span>
            </span>
            {q.list.length > 0 && (
              <>
                <span className="sq-bar" aria-hidden="true">
                  {q.status.map(([s, n]) => (
                    <span key={s} style={{ width: `${(n / q.list.length) * 100}%`, background: colour(s) }} title={`${s}: ${n}`} />
                  ))}
                </span>
                <span className="sq-status">
                  {q.status.map(([s, n]) => (
                    <span key={s}>
                      <i style={{ background: colour(s) }} />
                      {s} {n}
                    </span>
                  ))}
                </span>
                <span className="sq-flags">
                  <span className={q.unassigned ? 'is-flag' : undefined}>{q.unassigned} unassigned</span>
                  <span className={q.urgent ? 'is-flag' : undefined}>{q.urgent} critical/high</span>
                  <span className={q.aged ? 'is-flag' : undefined}>
                    {q.aged} over {AGED}d
                  </span>
                  <span>oldest {ageLabel(q.oldest)}</span>
                </span>
              </>
            )}
          </button>
        ))}
      </div>

      {open && (
        <section className="card">
          <div className="card-head">
            <div className="grow">
              <h3>
                {open.name} · {num(open.list.length)} open
              </h3>
              <p className="sub">Hover a cut-short title or name for all of it. Ticket numbers open in Autotask.</p>
            </div>
            <div className="row" style={{ gap: 'var(--space-2)' }}>
              <span className="xs muted">Sort</span>
              {(['age', 'priority', 'status'] as const).map((k) => (
                <button key={k} type="button" className={`btn btn-sm ${sort === k ? 'btn-secondary' : 'btn-ghost'}`} onClick={() => setSort(k)}>
                  {k === 'age' ? 'Oldest' : k === 'priority' ? 'Priority' : 'Status'}
                </button>
              ))}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPicked(null)}>
                Close
              </button>
            </div>
          </div>
          <table className="table sq-table">
            {/* Ticket number, status, priority and age keep their full width;
                the title, company and name share what is left. */}
            <colgroup>
              <col style={{ width: 150 }} />
              <col />
              <col style={{ width: '20%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 64 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Title</th>
                <th>Company</th>
                <th>Assigned</th>
                <th>Status</th>
                <th>Priority</th>
                <th className="num">Age</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const link = href(t);
                return (
                  <tr key={t.id}>
                    <td className="mono xs">
                      {link ? (
                        <a href={link} target="_blank" rel="noreferrer" title="Open in Autotask">
                          {t.ticketNumber}
                        </a>
                      ) : (
                        t.ticketNumber
                      )}
                    </td>
                    <td title={t.title}>{t.title || '—'}</td>
                    <td title={t.company ?? ''}>{t.company ?? '—'}</td>
                    <td title={t.assignedTo ?? ''}>{t.assignedTo ?? <span className="sq-flag-text">Unassigned</span>}</td>
                    <td title={t.status}>
                      <i className="sq-dot" style={{ background: colour(t.status) }} />
                      {t.status}
                    </td>
                    <td className={isUrgent(t.priority) ? 'sq-flag-text' : undefined} title={t.priority}>
                      {t.priority}
                    </td>
                    <td className={`num${(t.ageDays ?? 0) > AGED ? ' sq-flag-text' : ''}`}>{ageLabel(t.ageDays)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
